import { logger } from '../utils/logger';
/**
 * Custom hook for Entra External ID authentication using MSAL
 *
 * Provides a convenient interface for authentication operations
 * in React components.
 */

import { useCallback, useEffect, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import {
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import {
  loginRequest,
  tokenRequest,
  logoutRequest,
  getEntraUserInfo,
  EntraUserInfo,
} from '../config/authConfig';
import { clearAuthRedirectInProgress } from '../utils/authRedirect';

export interface UseMsalAuthResult {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  account: AccountInfo | null;
  userInfo: EntraUserInfo | null;
  error: Error | null;

  // Authentication methods
  login: (identityProvider?: string) => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string | null>;

  // Utility methods
  clearError: () => void;
}

/**
 * Hook for Entra External ID authentication
 *
 * @returns {UseMsalAuthResult} Authentication state and methods
 *
 * @example
 * ```tsx
 * const { isAuthenticated, login, logout, userInfo } = useMsalAuth();
 *
 * if (!isAuthenticated) {
 *   return <button onClick={login}>Sign In</button>;
 * }
 *
 * return <div>Welcome, {userInfo?.name}!</div>;
 * ```
 */
export const useMsalAuth = (): UseMsalAuthResult => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Derive authentication state
  const isAuthenticated = account !== null;
  const userInfo = account ? getEntraUserInfo(account) : null;

  /**
   * Initiate login flow using redirect
   *
   * @param identityProvider - Optional domain_hint value to redirect to specific provider directly
   *                           ('Google' for Google, 'microsoft.com' for Microsoft, or undefined for email/password)
   */
  const login = useCallback(
    async (identityProvider?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        // Prepare login parameters
        const loginParams: any = {
          ...loginRequest,
        };

        // Add extraQueryParameters to skip Entra page and go directly to provider
        // Using domain_hint parameter as per official Microsoft Entra External ID documentation
        // https://learn.microsoft.com/en-us/entra/external-id/customers/concept-authentication-methods-customers
        if (identityProvider) {
          loginParams.extraQueryParameters = {
            domain_hint: identityProvider,
          };
        }

        logger.trace('[useMsalAuth] Login parameters', {
          event: 'auth.login.params',
          identityProvider,
          hasDomainHint: !!loginParams.extraQueryParameters,
          scopes: loginParams.scopes,
        });

        // Use loginRedirect for production-grade experience
        await instance.loginRedirect(loginParams);

        // Note: Code after loginRedirect won't execute as user is redirected away
      } catch (err) {
        const error = err as Error;
        logger.error('[useMsalAuth] Login failed:', error);
        setError(error);
        setIsLoading(false);
      }
    },
    [instance]
  );

  /**
   * Initiate logout flow using MSAL's logoutRedirect API.
   *
   * This follows MSAL best practices (https://github.com/AzureAD/microsoft-
   * authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md):
   *
   * 1. Clears the local MSAL token cache.
   * 2. Navigates to Entra's logout endpoint (invalidates the server session).
   * 3. Entra redirects back to postLogoutRedirectUri (app root).
   *
   * Passing `account` lets MSAL extract `login_hint` from the ID token and
   * send it as `logout_hint`, which skips the account-picker prompt.
   *
   * IMPORTANT: The caller (useAuth.handleLogout) must NOT dispatch
   * clearEntraAccount() before this call. Setting isAuthenticated=false
   * causes React to render EntraRedirect, whose loginRedirect() races with
   * logoutRedirect — leading to interaction_in_progress errors or a stuck
   * "Redirecting to sign in..." spinner.
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Resolve the account to sign out of.
      const currentAccount = account || instance.getAllAccounts()[0] || undefined;

      // Clear any stale redirect-guard flags so EntraRedirect is not
      // blocked when the app reloads after the logout round-trip.
      clearAuthRedirectInProgress();

      // MSAL's logoutRedirect clears the local cache and navigates the
      // browser to Entra's end-session endpoint. The promise never
      // resolves because the browser navigates away.
      await instance.logoutRedirect({
        account: currentAccount,
        ...logoutRequest,
      });

      // Browser navigates away — code below is unreachable.
    } catch (err) {
      const error = err as Error;
      logger.error('[useMsalAuth] Logout failed:', error);
      setError(error);
      setIsLoading(false);
    }
  }, [instance, account]);

  /**
   * Acquire access token silently
   *
   * This method attempts to get a token from cache first,
   * then falls back to silent refresh, and finally to interactive login.
   *
   * @returns {Promise<string | null>} Access token or null if failed
   */
  const acquireToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      logger.warn('[useMsalAuth] No account available for token acquisition');
      return null;
    }

    try {
      // Attempt silent token acquisition
      const response: AuthenticationResult = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });

      return response.accessToken;
    } catch (err) {
      const error = err as Error;

      // Handle specific error types
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn('[useMsalAuth] Interaction required, initiating login redirect');

        try {
          // Interaction required - need to trigger login
          await instance.acquireTokenRedirect({
            ...tokenRequest,
            account: account,
          });
          return null;
        } catch (redirectError) {
          logger.error('[useMsalAuth] Token redirect failed:', redirectError);
          setError(redirectError as Error);
          return null;
        }
      } else {
        logger.error('[useMsalAuth] Token acquisition failed:', error);
        setError(error);
        return null;
      }
    }
  }, [instance, account]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Update loading state based on interaction progress
   */
  useEffect(() => {
    setIsLoading(inProgress !== InteractionStatus.None);
  }, [inProgress]);

  /**
   * Note: handleRedirectPromise() is now called in App.tsx
   * to ensure it's called only ONCE per page load.
   * Multiple calls can cause race conditions and authentication loops.
   */

  return {
    // State
    isAuthenticated,
    isLoading,
    account,
    userInfo,
    error,

    // Methods
    login,
    logout,
    acquireToken,
    clearError,
  };
};

export default useMsalAuth;
